import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    fetchStudents as fetchStudentsAPI,
    fetchProfile as fetchProfileAPI,
    fetchStudentTypes as fetchStudentTypesAPI,
    fetchStudentStatuses as fetchStudentStatusesAPI
} from '../utils/api';
import { getClassName } from '../utils/style';
import { ADMIN_ROLE, ADMISSION_STAFF, DSO_STAFF } from '../utils/roles';
import { mapErrorMessage } from '../utils/errorMapping';
import CreateStudentModal from './CreateStudentModal';
import './Students.css';
import './_Pagination.css';
import './_Buttons.css';
import './_Input.css';

const Students = () => {
    const navigate = useNavigate();
    const [students, setStudents] = useState([]);
    const [totalStudents, setTotalStudents] = useState(0);
    const [isCreateModalOpen, setCreateModalOpen] = useState(false);
    const [profile, setProfile] = useState(null);
    const [studentTypes, setStudentTypes] = useState({});
    const [studentStatuses, setStudentStatuses] = useState({});
    const [type, setType] = useState('');
    const [mainStatus, setMainStatus] = useState('');
    const [innerStatus, setInnerStatus] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);

    const fetchStudents = async () => {
        try {
            const currentFilters = {
                type: type,
                main_status: mainStatus,
                inner_status: innerStatus,
                page: page,
                limit: limit,
            };
            const data = await fetchStudentsAPI(currentFilters);
            setStudents(data.data || []);
            setTotalStudents(data.total || 0);
        } catch (error) {
            setError(mapErrorMessage(error.message));
        }
    };

    useEffect(() => {
        fetchStudents();
    }, [type, mainStatus, innerStatus, page, limit]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const profileData = await fetchProfileAPI();
                setProfile(profileData.data);
                const typesData = await fetchStudentTypesAPI();
                setStudentTypes(typesData.data);
                const statusesData = await fetchStudentStatusesAPI();
                setStudentStatuses(statusesData.data);
            } catch (error) {
                setError(mapErrorMessage(error.message));
            }
        };

        loadData();
    }, []);

    const handleRowClick = (studentId) => {
        navigate(`/students/${studentId}`);
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        if (name === 'type') {
            setType(value);
        } else if (name === 'main_status') {
            setMainStatus(value);
            setInnerStatus('');
        } else if (name === 'inner_status') {
            setInnerStatus(value);
        }
        setPage(1);
    };

    const handleOpenCreateModal = () => {
        setCreateModalOpen(true);
    };

    const handleCloseCreateModal = () => {
        setCreateModalOpen(false);
    };

    const handleStudentCreated = () => {
        fetchStudents();
    };

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= Math.ceil(totalStudents / limit)) {
            setPage(newPage);
        }
    };

    const canCreateStudent = profile && profile.roles.some(role => role.id === ADMIN_ROLE || role.id === ADMISSION_STAFF || role.id === DSO_STAFF);

    const totalPages = Math.ceil(totalStudents / limit) || 1;



    return (
        <div className="students-container">
            <div className="header">
                <h1>Students | {totalStudents}</h1>
                <div className="header-controls">
                    <div className="showing-control">
                        <label htmlFor="limit">Showing:</label>
                        <select id="limit" value={limit} onChange={handleLimitChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    {canCreateStudent && (
                        <button onClick={handleOpenCreateModal} className="add-student-button">+</button>
                    )}
                </div>
            </div>
            {error && <p className="error">{error}</p>}
            {message && <p className="message">{message}</p>}
            <table className="students-table">
                <colgroup>
                    <col style={{ width: '5%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '15%' }} />
                    <col style={{ width: '20%' }} />
                    <col style={{ width: '20%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>
                            <div className="th-content">
                                <select name="type" value={type} onChange={handleFilterChange}>
                                    <option value="">Type</option>
                                    {Object.entries(studentTypes).map(([id, name]) => (
                                        <option key={id} value={id}>{name}</option>
                                    ))}
                                </select>
                            </div>
                        </th>
                        <th>
                            <div className="th-content">
                                <select name="main_status" value={mainStatus} onChange={handleFilterChange}>
                                    <option value="">Status</option>
                                    {Object.entries(studentStatuses).map(([id, statuses]) => (
                                        <option key={id} value={id}>{statuses[0]}</option>
                                    ))}
                                </select>
                            </div>
                        </th>
                        <th>
                            <div className="th-content">
                                <select name="inner_status" value={innerStatus} onChange={handleFilterChange} disabled={!mainStatus}>
                                    <option value="">Sub-Status</option>
                                    {mainStatus && studentStatuses[mainStatus] &&
                                        Object.entries(studentStatuses[mainStatus]).map(([id, name]) => {
                                            if (id !== "0") {
                                                return <option key={id} value={id}>{name}</option>
                                            }
                                            return null;
                                        })
                                    }
                                </select>
                            </div>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {students.map(student => (
                        <tr key={student.id} onClick={() => handleRowClick(student.id)}>
                            <td>{student.inner_id}</td>
                            <td><strong>{student.first_name}</strong></td>
                            <td><strong>{student.last_name}</strong></td>
                            <td>
                                <span className={`student-type ${getClassName('type', student.type.name)}`}>
                                    {student.type.name}
                                </span>
                            </td>
                            <td>
                                <span className={`student-status ${getClassName('status', student.status.mss_name)}`}>
                                    {student.status.mss_name}
                                </span>
                            </td>
                            <td>
                                <span className={`student-inner-status ${getClassName('inner-status', student.status.iss_name)}`}>
                                    {student.status.iss_name}
                                </span>
                            </td>
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan="6">
                            <div className="pagination-controls">
                                <div className="page-buttons">
                                    <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                        {'<'}
                                    </button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                        {'>'}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
            {isCreateModalOpen && (
                <CreateStudentModal
                    onClose={handleCloseCreateModal}
                    onStudentCreated={handleStudentCreated}
                />
            )}
        </div>
    );
};

export default Students;